import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowDown, ArrowUp, ChevronDown, ChevronRight } from 'lucide-react'
import DealDetail from '@/components/DealDetail'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/layout/Card'
import TableCard from '@/components/layout/TableCard'
import Select from '@/components/ui/Select'
import TrendChart, { type TrendMetric } from '@/features/dashboard/TrendChart'
import { useDashboard } from '@/features/dashboard/useDashboard'
import { useEmailEngagement } from '@/features/analytics/useAnalytics'
import { usePipelines } from '@/features/board/useBoard'
import { useCurrency } from '@/features/agency/useCurrency'
import { formatCompactValue, formatDealValue } from '@/lib/dealFormat'
import { HEALTH_DOT, HEALTH_META } from '@/lib/dealHealth'
import { cn } from '@/lib/cn'
import type {
  Dashboard as DashboardData,
  DashboardActivity,
  DashboardHealthKey,
  DashboardPeriod,
  DashboardSectionRow,
  DashboardTeamRow,
} from '@/api/dashboard'

const PERIODS: { value: DashboardPeriod; label: string; short: string }[] = [
  { value: 'week', label: '7 days', short: '7d' },
  { value: 'month', label: '30 days', short: '30d' },
  { value: 'quarter', label: '90 days', short: '90d' },
  { value: 'year', label: '12 months', short: '12m' },
]

/** Health states in the order a manager acts on them: the at-risk three first. */
const HEALTH_ORDER: { key: DashboardHealthKey; field: keyof DashboardData['health'] }[] = [
  { key: 'overdue', field: 'overdue' },
  { key: 'no_follow_up', field: 'noFollowUp' },
  { key: 'stalled', field: 'stalled' },
  { key: 'due_soon', field: 'dueSoon' },
  { key: 'on_track', field: 'onTrack' },
]

/** Meter track: a light step of the state's own hue, so the state reads across the whole bar. */
const HEALTH_TRACK: Record<string, string> = {
  red: 'bg-red-100',
  amber: 'bg-amber-100',
  gray: 'bg-gray-100',
  green: 'bg-emerald-100',
}

const AT_RISK_PREVIEW = 5

// --- figures ----------------------------------------------------------------------------------

/** Change against the window before. `amount` is null when nothing changed. */
interface Delta {
  direction: 'up' | 'down' | 'flat'
  amount: string | null
  span: string
}

/**
 * Deal counts at an agency are small, so their change is the plain difference: going from 1 to 7 new
 * deals reads "+6", not a "600%" that sounds like a boom.
 */
function countChange(current: number, previous: number, span: string): Delta | null {
  if (current === 0 && previous === 0) return null
  if (current === previous) return { direction: 'flat', amount: null, span }
  const diff = current - previous
  return {
    direction: diff > 0 ? 'up' : 'down',
    amount: String(Math.abs(diff)),
    span,
  }
}

/** Money reads as a percentage — or as the plain amount when the window before had nothing to compare. */
function moneyChange(
  current: number,
  previous: number,
  span: string,
  format: (n: number) => string,
): Delta | null {
  if (current === 0 && previous === 0) return null
  if (current === previous) return { direction: 'flat', amount: null, span }
  const up = current > previous
  const amount =
    previous === 0
      ? format(current)
      : `${Math.round((Math.abs(current - previous) / previous) * 100)}%`
  return { direction: up ? 'up' : 'down', amount, span }
}

/** Win rate moves in percentage points — "4 pts", not a percentage of a percentage. */
function winRateChange(
  current: number | null,
  previous: number | null,
  span: string,
): Delta | null {
  if (current == null || previous == null) return null
  const points = Math.round((current - previous) * 100)
  if (points === 0) return { direction: 'flat', amount: null, span }
  return { direction: points > 0 ? 'up' : 'down', amount: `${Math.abs(points)} pts`, span }
}

/**
 * A headline figure: label, value, a supporting line, and — for period figures — the change against
 * the window before. Up is good for every figure that carries a change, so up reads green.
 */
function StatTile({
  label,
  value,
  title,
  sub,
  delta,
  icon,
}: {
  label: string
  value: string
  /** Full figure on hover when `value` is shortened. */
  title?: string
  sub?: ReactNode
  delta?: Delta | null
  icon?: ReactNode
}) {
  return (
    <div className="h-full bg-white border border-gray-200 rounded-xl p-4 min-w-0">
      <div className="text-xs font-medium text-gray-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold text-brand-darkest mt-1 truncate" title={title}>
        {value}
      </div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
      {delta && (
        // Only the arrow and the amount carry the direction colour; the context stays in body ink.
        // The line wraps rather than truncating — a clipped "vs previous 30 d…" says nothing.
        <div className="text-xs mt-1.5 flex items-start gap-1">
          {delta.direction !== 'flat' && (
            <span
              className={cn(
                'mt-px shrink-0 flex items-center gap-0.5 font-medium',
                delta.direction === 'up' ? 'text-emerald-700' : 'text-red-600',
              )}
            >
              {delta.direction === 'up' ? (
                <ArrowUp size={12} aria-label="Up" />
              ) : (
                <ArrowDown size={12} aria-label="Down" />
              )}
              {delta.amount}
            </span>
          )}
          <span className="text-gray-500">
            {delta.direction === 'flat' ? 'Same as' : 'vs'} previous {delta.span}
          </span>
        </div>
      )}
    </div>
  )
}

// --- sections ---------------------------------------------------------------------------------

function SectionsCard({
  sections,
  currency,
}: {
  sections: DashboardSectionRow[]
  currency: string
}) {
  const [open, setOpen] = useState<string | null>(null)
  const max = Math.max(1, ...sections.map((s) => s.count))
  return (
    <Card title="Pipeline by section">
      {sections.length === 0 ? (
        <p className="text-sm text-gray-400">No open deals in view.</p>
      ) : (
        <div className="space-y-1">
          {sections.map((s) => {
            const key = `${s.pipelineName ?? ''}:${s.sectionId ?? 'none'}`
            const expanded = open === key
            return (
              <div key={key}>
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : key)}
                  aria-expanded={expanded}
                  className="w-full text-left rounded-lg px-2 py-2 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
                >
                  <div className="flex items-center gap-2">
                    {expanded ? (
                      <ChevronDown size={14} className="text-gray-400 shrink-0" />
                    ) : (
                      <ChevronRight size={14} className="text-gray-400 shrink-0" />
                    )}
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0 bg-gray-300"
                      style={s.color ? { backgroundColor: s.color } : undefined}
                    />
                    {/* The name keeps a floor width: on a narrow screen it truncates, it never vanishes. */}
                    <span className="flex-1 min-w-[5rem] truncate text-sm font-medium text-brand-dark">
                      {s.name}
                      {s.pipelineName && (
                        <span className="font-normal text-xs text-gray-400">
                          {' '}
                          · {s.pipelineName}
                        </span>
                      )}
                    </span>
                    <span className="text-sm text-gray-500 tabular-nums shrink-0">
                      {s.count}{' '}
                      <span className="hidden sm:inline">{s.count === 1 ? 'deal' : 'deals'}</span>
                    </span>
                    <span className="sm:w-20 text-right text-sm font-semibold text-brand-dark tabular-nums shrink-0">
                      {formatCompactValue(s.value, currency)}
                    </span>
                  </div>
                  <div className="mt-1.5 ml-[38px] sm:mr-[88px] h-1.5 rounded-full bg-brand-bg overflow-hidden">
                    <div
                      className="h-full rounded-full bg-brand-primary"
                      style={{ width: `${(s.count / max) * 100}%` }}
                    />
                  </div>
                </button>
                {expanded && (
                  <ul className="ml-[46px] mr-2 mb-2 mt-0.5 space-y-1">
                    {s.stages.map((st) => (
                      <li
                        key={st.stageId}
                        className="flex items-center gap-2 text-xs text-gray-600"
                      >
                        <span className="flex-1 min-w-0 truncate">{st.name}</span>
                        <span className="tabular-nums shrink-0">{st.count}</span>
                        <span className="sm:w-20 text-right tabular-nums shrink-0">
                          {formatCompactValue(st.value, currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// --- at risk ----------------------------------------------------------------------------------

function AtRiskCard({
  attention,
  openDealCount,
  currency,
  onOpenDeal,
}: {
  attention: DashboardData['attention']
  openDealCount: number
  currency: string
  onOpenDeal: (id: string) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const shown = showAll ? attention.deals : attention.deals.slice(0, AT_RISK_PREVIEW)
  return (
    <Card
      title={
        <span id="at-risk" className="flex items-center gap-1.5">
          Need a follow-up
        </span>
      }
      action={
        attention.total > 0 && (
          <span className="text-xs text-gray-500">
            {attention.total} of {openDealCount} open {openDealCount === 1 ? 'deal' : 'deals'}
          </span>
        )
      }
    >
      {attention.total === 0 ? (
        <p className="text-sm text-gray-500">Every open deal has a follow-up in hand.</p>
      ) : (
        <>
          <ul className="divide-y divide-gray-100 -my-2">
            {shown.map((d) => {
              const tone = HEALTH_META[d.health].tone
              return (
                <li key={d.dealId}>
                  <button
                    type="button"
                    onClick={() => onOpenDeal(d.dealId)}
                    className="w-full text-left py-2.5 flex items-start gap-2.5 rounded-lg hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
                  >
                    <span
                      className={cn('mt-1.5 w-2 h-2 rounded-full shrink-0', HEALTH_DOT[tone])}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium text-brand-dark truncate">
                          {d.title}
                        </span>
                        {d.value != null && (
                          <span className="ml-auto text-xs text-gray-500 tabular-nums shrink-0">
                            {formatCompactValue(d.value, currency)}
                          </span>
                        )}
                      </span>
                      {/* The reason names the state itself ("…overdue", "No follow-up…", "Stalled for…"),
                          so the state label isn't repeated in front of it. */}
                      <span className="block text-xs text-gray-500 truncate">
                        {d.reason}
                        {d.ownerName ? ` · ${d.ownerName}` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
          {attention.deals.length > AT_RISK_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-3 text-xs font-medium text-brand-primary hover:text-brand-dark"
            >
              {showAll ? 'Show fewer' : `Show all ${attention.total}`}
            </button>
          )}
          {showAll && attention.total > attention.deals.length && (
            <p className="mt-1 text-[11px] text-gray-400">
              Showing the {attention.deals.length} most urgent of {attention.total}.
            </p>
          )}
        </>
      )}
    </Card>
  )
}

// --- health -----------------------------------------------------------------------------------

/**
 * "How am I doing?" in effort, not just outcomes: what was logged or sent in the period — emails, SMS,
 * calls, notes (and meetings, once any) — each against the period before.
 */
function ActivityStrip({ activity: a, title, windowLabel }: { activity: DashboardActivity; title: string; windowLabel: string }) {
  const items = [
    { label: 'Emails sent', now: a.emails, before: a.emailsPrevious },
    { label: 'SMS sent', now: a.sms, before: a.smsPrevious },
    { label: 'Calls logged', now: a.calls, before: a.callsPrevious },
    { label: 'Notes', now: a.notes, before: a.notesPrevious },
    ...(a.meetings || a.meetingsPrevious ? [{ label: 'Meetings', now: a.meetings, before: a.meetingsPrevious }] : []),
  ]
  return (
    <section>
      <h2 className="text-sm font-semibold text-brand-darkest mb-3">{title} · last {windowLabel}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((i) => (
          <StatTile key={i.label} label={i.label} value={String(i.now)} delta={countChange(i.now, i.before, windowLabel)} />
        ))}
      </div>
    </section>
  )
}

function HealthCard({ health, total }: { health: DashboardData['health']; total: number }) {
  return (
    <Card title="Pipeline health">
      {total === 0 ? (
        <p className="text-sm text-gray-400">No open deals in view.</p>
      ) : (
        <ul className="space-y-3">
          {HEALTH_ORDER.map(({ key, field }) => {
            const n = health[field]
            const { label, tone } = HEALTH_META[key]
            const pct = total ? Math.round((n / total) * 100) : 0
            return (
              <li key={key}>
                <div className="flex items-center gap-2 text-sm">
                  <span className={cn('w-2 h-2 rounded-full shrink-0', HEALTH_DOT[tone])} />
                  <span className="flex-1 min-w-0 truncate whitespace-nowrap text-brand-dark">
                    {label}
                  </span>
                  <span className="tabular-nums font-medium text-brand-dark">{n}</span>
                  <span className="w-10 text-right tabular-nums text-xs text-gray-400">{pct}%</span>
                </div>
                <div
                  className={cn(
                    'mt-1 ml-4 mr-12 h-1.5 rounded-full overflow-hidden',
                    HEALTH_TRACK[tone],
                  )}
                >
                  <div
                    className={cn('h-full rounded-full', HEALTH_DOT[tone])}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

// --- trend ------------------------------------------------------------------------------------

function TrendCard({ trend, currency }: { trend: DashboardData['trend']; currency: string }) {
  const [metric, setMetric] = useState<TrendMetric>('won')
  return (
    <Card
      title={metric === 'won' ? 'Won business by month' : 'New business by month'}
      action={
        <div
          className="inline-flex rounded-lg border border-gray-200 p-0.5"
          role="group"
          aria-label="Measure"
        >
          {(['won', 'new'] as TrendMetric[]).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={metric === m}
              onClick={() => setMetric(m)}
              className={cn(
                'px-2.5 py-1 text-xs rounded-md transition-colors',
                metric === m
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-500 hover:text-brand-dark',
              )}
            >
              {m === 'won' ? 'Won' : 'New deals'}
            </button>
          ))}
        </div>
      }
    >
      <TrendChart months={trend} metric={metric} currency={currency} />
    </Card>
  )
}

// --- team -------------------------------------------------------------------------------------

function TeamTable({
  rows,
  currency,
  canNarrowTo,
  onNarrow,
}: {
  rows: DashboardTeamRow[]
  currency: string
  canNarrowTo: Set<string>
  onNarrow: (userId: string) => void
}) {
  const pct = (r: number | null) => (r == null ? '—' : `${Math.round(r * 100)}%`)
  return (
    <TableCard>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="font-medium px-4 py-2.5">Agent</th>
            <th className="font-medium px-4 py-2.5 text-right">Open deals</th>
            <th className="font-medium px-4 py-2.5 text-right">Pipeline</th>
            <th className="font-medium px-4 py-2.5 text-right">New</th>
            <th className="font-medium px-4 py-2.5 text-right">Won</th>
            <th className="font-medium px-4 py-2.5 text-right">Win rate</th>
            <th className="font-medium px-4 py-2.5 text-right">Overdue tasks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.userId ?? 'unassigned'} className="border-b border-gray-100 last:border-0">
              <td className="px-4 py-2.5 font-medium">
                {r.userId && canNarrowTo.has(r.userId) ? (
                  <button
                    type="button"
                    onClick={() => onNarrow(r.userId as string)}
                    title={`Show ${r.name}'s numbers`}
                    className="text-brand-dark hover:text-brand-primary hover:underline text-left"
                  >
                    {r.name}
                  </button>
                ) : (
                  <span className={r.userId ? 'text-brand-dark' : 'text-gray-500'}>{r.name}</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{r.openDealCount}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatDealValue(r.openPipelineValue, currency)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{r.newDealCount}</td>
              <td
                className="px-4 py-2.5 text-right tabular-nums"
                title={formatDealValue(r.wonValue, currency)}
              >
                {r.wonCount}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{pct(r.winRate)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {r.overdueTaskCount > 0 ? (
                  <span className="text-red-600 font-medium">{r.overdueTaskCount}</span>
                ) : (
                  <span className="text-gray-300">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableCard>
  )
}

// --- page -------------------------------------------------------------------------------------

/**
 * The dashboard answers four questions in order — how are we performing, what is in the pipeline,
 * is anything at risk, where should I look next — and points to the page where the work happens
 * rather than becoming one. One filter row scopes everything beneath it.
 */
export default function Dashboard() {
  const [period, setPeriod] = useState<DashboardPeriod>('month')
  const [ownerId, setOwnerId] = useState<string | null>(null)
  const [pipelineId, setPipelineId] = useState<string | null>(null)
  const [selectedDeal, setSelectedDeal] = useState<string | null>(null)
  const { data, isLoading, isError, isPlaceholderData } = useDashboard({
    period,
    ownerId,
    pipelineId,
  })
  const { data: pipelines } = usePipelines()
  const { data: engagement } = useEmailEngagement()
  const currency = useCurrency()

  const windowLabel = PERIODS.find((p) => p.value === period)?.label ?? '30 days'
  const owners = data?.scope.owners ?? []
  const ownerName = ownerId ? owners.find((o) => o.id === ownerId)?.name : null

  return (
    <PageContainer width="full">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-brand-darkest">Dashboard</h1>
        <p className="text-sm text-gray-500">
          {ownerName ? `${ownerName}'s` : 'How your'} pipeline is tracking — last {windowLabel},
          compared with the {windowLabel} before.
        </p>
      </div>

      {/* One filter row, above everything it scopes. The period comes first. */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div
          className="inline-flex max-w-full overflow-x-auto rounded-lg border border-gray-200 bg-white p-0.5"
          role="group"
          aria-label="Period"
        >
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              aria-pressed={period === p.value}
              aria-label={`Last ${p.label}`}
              onClick={() => setPeriod(p.value)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md whitespace-nowrap transition-colors',
                period === p.value
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-500 hover:text-brand-dark',
              )}
            >
              <span className="sm:hidden">{p.short}</span>
              <span className="hidden sm:inline">{p.label}</span>
            </button>
          ))}
        </div>
        {owners.length > 1 && (
          <Select
            aria-label="Person"
            value={ownerId ?? ''}
            onChange={(e) => setOwnerId(e.target.value || null)}
            className="min-w-[10rem]"
          >
            <option value="">Everyone</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        )}
        {(pipelines?.length ?? 0) > 1 && (
          <Select
            aria-label="Pipeline"
            value={pipelineId ?? ''}
            onChange={(e) => setPipelineId(e.target.value || null)}
            className="min-w-[10rem]"
          >
            <option value="">All pipelines</option>
            {pipelines?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        )}
        {(ownerId || pipelineId) && (
          <button
            type="button"
            onClick={() => {
              setOwnerId(null)
              setPipelineId(null)
            }}
            className="text-xs text-gray-500 hover:text-brand-dark px-1"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
      {isError && !data && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-danger text-sm">
          Could not load the dashboard. Please try again.
        </div>
      )}

      {data && (
        // While a filter change loads, the current numbers stay put, dimmed — no flash, no jump.
        <div className={cn('space-y-6 transition-opacity', isPlaceholderData && 'opacity-60')}>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <StatTile
              label="Open pipeline"
              value={formatCompactValue(data.kpi.openPipelineValue, currency)}
              title={formatDealValue(data.kpi.openPipelineValue, currency)}
              sub={`${data.kpi.openDealCount} open ${data.kpi.openDealCount === 1 ? 'deal' : 'deals'}`}
            />
            <StatTile
              label="New deals"
              value={String(data.kpi.newDealCount)}
              sub={`Last ${windowLabel}`}
              delta={countChange(data.kpi.newDealCount, data.kpi.newDealCountPrevious, windowLabel)}
            />
            <StatTile
              label="Won"
              value={formatCompactValue(data.kpi.wonValue, currency)}
              title={formatDealValue(data.kpi.wonValue, currency)}
              sub={`${data.kpi.wonCount} ${data.kpi.wonCount === 1 ? 'deal' : 'deals'}`}
              delta={moneyChange(data.kpi.wonValue, data.kpi.wonValuePrevious, windowLabel, (n) =>
                formatCompactValue(n, currency),
              )}
            />
            <StatTile
              label="Win rate"
              value={data.kpi.winRate == null ? '—' : `${Math.round(data.kpi.winRate * 100)}%`}
              sub={data.kpi.winRate == null ? 'Nothing closed yet' : 'Of deals closed'}
              delta={winRateChange(data.kpi.winRate, data.kpi.winRatePrevious, windowLabel)}
            />
            <a
              href="#at-risk"
              className="block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
            >
              <StatTile
                label="Need a follow-up"
                icon={
                  data.attention.total > 0 ? (
                    <AlertTriangle size={12} className="text-amber-600" aria-hidden />
                  ) : undefined
                }
                value={String(data.attention.total)}
                sub={`of ${data.kpi.openDealCount} open ${data.kpi.openDealCount === 1 ? 'deal' : 'deals'}`}
              />
            </a>
            <Link
              to="/focus?tab=tasks"
              className="block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
            >
              <StatTile
                label="Overdue tasks"
                value={String(data.kpi.overdueTaskCount)}
                sub={<span className="text-brand-primary">Open in Focus →</span>}
              />
            </Link>
          </div>

          {data.activity && (
            <ActivityStrip
              activity={data.activity}
              title={ownerId ? `${ownerName ?? 'Their'} activity` : data.team.length > 1 ? 'Team activity' : 'Your activity'}
              windowLabel={windowLabel}
            />
          )}

          {/* What's in the pipeline (left) beside what in it needs acting on (right). */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="space-y-6 min-w-0">
              <SectionsCard sections={data.sections} currency={currency} />
              <HealthCard health={data.health} total={data.kpi.openDealCount} />
            </div>
            <AtRiskCard
              attention={data.attention}
              openDealCount={data.kpi.openDealCount}
              currency={currency}
              onOpenDeal={setSelectedDeal}
            />
          </div>

          <TrendCard trend={data.trend} currency={currency} />

          {data.team.length > 1 && (
            <section>
              <h2 className="text-sm font-semibold text-brand-darkest mb-3">
                Team performance · last {windowLabel}
              </h2>
              <TeamTable
                rows={data.team}
                currency={currency}
                canNarrowTo={new Set(owners.map((o) => o.id))}
                onNarrow={setOwnerId}
              />
            </section>
          )}

          {/* Kept, but small and last: for most agents email numbers aren't the first thing they need. */}
          {/* Agency-wide email figures don't follow the person/pipeline filters, so they only show
              when nothing is narrowed — a filter must scope everything beneath it. */}
          {engagement && engagement.sent > 0 && !ownerId && !pipelineId && (
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span className="text-xs font-medium text-gray-500">Email · last 30 days</span>
              <span>
                <span className="font-semibold text-brand-darkest">{engagement.sent}</span>{' '}
                <span className="text-gray-500">sent</span>
              </span>
              <span>
                <span className="font-semibold text-brand-darkest">
                  {Math.round(engagement.openRate * 100)}%
                </span>{' '}
                <span className="text-gray-500">opened</span>
              </span>
              <span>
                <span className="font-semibold text-brand-darkest">
                  {Math.round(engagement.clickRate * 100)}%
                </span>{' '}
                <span className="text-gray-500">clicked</span>
              </span>
            </div>
          )}
        </div>
      )}

      {selectedDeal && <DealDetail dealId={selectedDeal} onClose={() => setSelectedDeal(null)} />}
    </PageContainer>
  )
}
